import React from "react";
import { Box, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ViewContainer from "../../components/ViewContainer";
import DashboardGrid from "../../components/grid/DashboardGrid";
import Panel from "../../components/grid/Panel";
import KpiStat from "../../components/kpi/KpiStat";
import FunnelChart from "../../components/charts/FunnelChart";
import CountUp from "../../components/CountUp";
import { formatInt } from "../../utils/format";
import { resolveByType } from "../../tenant/tenant";
import overviewData from "../../data/overview.json";
import journeyData from "../../data/journey.json";

// HU-1 — Resumen: panel de entrada con KPIs clave + mini-embudo + accesos.
function OverviewView() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const data = resolveByType(overviewData);
    const journey = resolveByType(journeyData);

    const funnelRows = journey.root.funnel.map((f) => ({
        label: t(`phases.${f.phaseKey}`),
        retentionPct: f.retentionPct,
        count: f.count,
        caidaPp: f.dropPp,
        critical: f.critical,
        note: null,
    }));

    const links = [
        { key: "fuga", to: "/journey" },
        { key: "perfiles", to: "/perfiles" },
        { key: "senales", to: "/conductual" },
    ];

    return (
        <ViewContainer title={t("views.overview.title")} description={t("views.overview.description")}>
            <DashboardGrid gap={2} rowGap={3}>
                <Panel span={12} dense>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 2, sm: 3 } }}>
                        <Kpi first tone="accent" label={t("views.overview.kpi.fugaLabel")}
                            value={<CountUp to={data.fugaPct} suffix="%" />} hint={t("views.overview.kpi.fugaHint")} />
                        <Kpi tone="default" label={t("views.overview.kpi.worstLabel")}
                            value={t("views.overview.kpi.worstValue")} hint={t("views.overview.kpi.worstHint")} />
                        <Kpi tone="default" label={t("views.overview.kpi.cohorteLabel")}
                            value={<CountUp to={data.cohorteSize} format={formatInt} />} hint={t("views.overview.kpi.cohorteHint")} />
                    </Box>
                </Panel>

                <Panel span={{ xs: 12, md: 8 }} dense title={t("views.overview.funnelTitle")}>
                    <FunnelChart data={funnelRows} barHeight={26} dropUnit="%" />
                </Panel>

                <Panel span={{ xs: 12, md: 4 }} dense title={t("views.overview.linksTitle")}>
                    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 2 }}>
                        <span />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {links.map((l) => (
                                <Box
                                    key={l.key}
                                    onClick={() => navigate(l.to)}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        py: 1,
                                        px: 1.25,
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        color: "primary.main",
                                        transition: "background-color 0.15s ease",
                                        "&:hover": { bgcolor: "rgba(50,72,202,0.06)" },
                                    }}
                                >
                                    <Typography sx={{ fontSize: 14 }}>{t(`views.overview.links.${l.key}`)}</Typography>
                                    <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Panel>
            </DashboardGrid>
        </ViewContainer>
    );
}

function Kpi({ label, value, hint, tone, first }) {
    return (
        <Box sx={{ flex: 1, ...(!first && { borderLeft: { sm: "1px solid" }, borderColor: { sm: "divider" }, pl: { sm: 3 } }) }}>
            <KpiStat label={label} value={value} tone={tone} hint={hint} />
        </Box>
    );
}

export default OverviewView;
